import React, { useEffect, useState } from 'react'
import { Row, Col, Form, Modal, Input, Button, Select, DatePicker, Radio, TimePicker } from 'antd'
import '../styles/modal-styles.scss'
import closeIcon from 'assets/Images/close.svg'
import DateandTimeInputs from '../../../../GeneralDetails/InputComps/DateandTimeInputs'
import TicketColors from '../TicketColors'
import { AdmissionTicketPayloadTypes, TicketsResp } from 'types/event/eventStep4'
import moment, { Moment } from 'moment'
import { convertMinutesToLocalTime, convertTimeToUtcMinutes } from 'helper/utility'
import {
  useEditStep6AdmissionTicketMutation,
  useEditStep6AdditionalOptionsMutation,
} from 'api/Classes/Step6Tickets/Tickets'
import { classesEventStore } from 'state/classes/classesStore'
import ButtonSpinner from 'components/global/ButtonSpinner'
import { infoMessage } from 'api/global'
import { Photo, SpecialSchedule } from 'types/globalTypes'
import UploadDocument from 'components/global/UploadDocument'

interface AdmissionTicketModalProps {
  modalOpen: boolean
  modalClose: React.Dispatch<React.SetStateAction<boolean>>
  type: 'admission' | 'additional'
  data?: TicketsResp
}

const AdmissionTicket: React.FC<AdmissionTicketModalProps> = ({
  modalOpen,
  modalClose,
  type,
  data,
}) => {
  const [form] = Form.useForm()
  const classesData = classesEventStore(store => store.classesData) as any
  const setClassesData = classesEventStore(store => store.setClassesData)
  const [selectedDates, setSelectedDates] = useState<string[]>([])
  const [specialScheduleData, setSpecialScheduleData] = useState([])
  const [ticketType, setTicketType] = useState<any>(data ? data.ticketType : undefined)
  const [period, setPeriod] = useState<'weekly' | 'monthly' | undefined>(
    data ? (data as any).period : undefined,
  )
  const [eventTime, setEventTime] = useState({
    startTime: undefined,
    endTime: undefined,
  })
  const [defaultTime, setDefaultTime] = useState({
    startTime: moment('09:00', 'HH:mm'),
    endTime: moment('15:00', 'HH:mm'),
  })

  const [ticketColor, setTicketColor] = useState<string>(data ? data.color : undefined)
  const [admissionTicketId, setAdmissionTicketId] = useState<string | undefined>(
    type === 'additional' ? (data as any)?.allowedTicketsIds?.[0] : undefined,
  )
  const [documentIds, setDocumentIds] = useState<string[]>(data?.photoId ? [data.photoId] : [])
  const [photos, setPhotos] = useState<Photo[]>(data?.photo ? [data.photo] : [])

  const { mutate: admissionTicketMutate, isLoading: admissionTicketLoading } =
    useEditStep6AdmissionTicketMutation()
  const { mutate: additionalTicketMutate, isLoading: additionalTicketLoading } =
    useEditStep6AdditionalOptionsMutation()

  const formSchema = {
    name: data ? data.name : undefined,
    ticketType: data ? data.ticketType : undefined,
    period: data && (data as any).ticketType === 'grouped' ? (data as any).period : undefined,
    expiry: (data as any)?.expiry ? moment((data as any).expiry) : undefined,
    ['expiry-time']: (data as any)?.expiry ? moment((data as any).expiry) : undefined,
    numberOfDays: data ? (data as any)?.numberOfDays : undefined,
    numberOfSelection: data ? (data as any)?.numberOfSelection : undefined,
    description: data ? data.description : undefined,
    price: data ? data.price : undefined,
    allowedTicketsIds: data ? (data as any).allowedTicketsIds : [],
    guestAllowedToVisit: data ? (data as any)?.guestAllowedToVisit : 'all',
    minAge: data ? (data as any)?.minAge : undefined,
    maxAge: data ? (data as any)?.maxAge : undefined,
    limitPerFamily: data ? (data as any)?.limitPerFamily : undefined,
    totalLimit: data ? (data as any)?.totalLimit : undefined,
  }

  const handleCancel = () => {
    modalClose(false)
  }

  const handleTicketType = (value: string) => {
    setTicketType(value)
  }

  const handlePeriodChange = (value: 'weekly' | 'monthly') => {
    setPeriod(value)
  }

  const handleAdmissionTicketChange = (value: string) => {
    setAdmissionTicketId(value)
  }

  const disableOutOfRangeDates = (current: Moment) => {
    if (!classesData?.datesOfEvent || classesData.datesOfEvent.length === 0) {
      return false // Don't disable any dates if no datesOfEvent is set
    }
    const firstDate = moment(classesData.datesOfEvent[0]).startOf('day')
    const lastDate = moment(
      classesData.datesOfEvent[classesData.datesOfEvent.length - 1],
    ).endOf('day')
    return current < firstDate || current > lastDate
  }

  const onFinish = (values: AdmissionTicketPayloadTypes) => {
    if (!values.name) return infoMessage('Name is required')
    if (type === 'additional' && !values.allowedTicketsIds) {
      return infoMessage('Allowed ticket is required')
    }
    if (!values.ticketType) return infoMessage('Ticket type is required')
    if (values.ticketType === 'grouped' && !values.period) return infoMessage('Period is required')
    if (values.ticketType !== 'one-time' && selectedDates.length === 0) {
      return infoMessage('Ticket days and time is required')
    }
    if (values.price === undefined || values.price === null) {
      return infoMessage('Ticket price is required')
    }
    if (!ticketColor) return infoMessage('Ticket color is required')
    if (!values.guestAllowedToVisit) {
      return infoMessage('Guest allowed to visit is required')
    }
    if (values.numberOfDays && values.numberOfDays > selectedDates.length) {
      return infoMessage('Max number of days must be equal to less than ticket days')
    }
    if (ticketType === 'schedule' && values.numberOfDays > 7) {
      return infoMessage('Max number of days must be less than or equal to 7')
    }
    if (!values.numberOfDays && (ticketType === 'open' || ticketType === 'schedule')) {
      return infoMessage('Max number of days is required')
    }
    if (!values.numberOfSelection && ticketType === 'grouped') {
      return infoMessage('Number of selection is required')
    }

    if (ticketType === 'grouped' && values.numberOfSelection && selectedDates.length) {
      const uniqueWeekKeys = new Set(
        selectedDates.map(date => moment(date).format('GGGG-[W]WW')),
      )
      const uniqueMonthKeys = new Set(selectedDates.map(date => moment(date).format('YYYY-MM')))

      if (values.period === 'weekly' && values.numberOfSelection > uniqueWeekKeys.size) {
        return infoMessage('Max number of weeks must be less than or equal to selected weeks')
      }
      if (values.period === 'monthly' && values.numberOfSelection > uniqueMonthKeys.size) {
        return infoMessage('Max number of months must be less than or equal to selected months')
      }
    }

   const payload: any = { // Changed to any to handle dynamic cleanup
      ...values,
      minAge: !values.minAge ? undefined : Number(values.minAge),
      maxAge: !values.maxAge ? undefined : Number(values.maxAge),
      limitPerFamily: !values.limitPerFamily ? undefined : Number(values.limitPerFamily),
      totalLimit: !values.totalLimit ? undefined : Number(values.totalLimit),
      color: ticketColor,
    
      // Keep the selected calendar day as-is. Going through `new Date(...Z)` here
      // reinterpreted the local date string as UTC, which shifted the day back one
      // for negative-offset (US) timezones (Mon–Fri saved as Sun–Thu). Parse the
      // YYYY-MM-DD string directly and set the start hour on it — same pattern as
      // the special-schedule mapping below — so the day never shifts.
      days: (selectedDates || []).map((item: string) =>
        moment(item, 'YYYY-MM-DD')
          .set({
            hour: parseInt(moment(eventTime.startTime).utc().format('HH:mm'), 10),
            minute: 0,
            second: 0,
            millisecond: 0,
          })
          .format('YYYY-MM-DDTHH:mm:ss[Z]'),
      ),

      additionalOptions: classesData?.additionalOptions || [],
      admissionTickets: classesData?.admissionTickets || [],

      timings: {
        startTime: convertTimeToUtcMinutes(eventTime.startTime),
        endTime: convertTimeToUtcMinutes(eventTime.endTime),
        specialSchedules: (specialScheduleData || []).map((d: SpecialSchedule) => ({
          ...d,
          day: moment(d.day, 'YYYY-MM-DD').format('YYYY-MM-DDTHH:mm:ss[Z]'),
        })),
      },
      expiry:
        (values as any).expiry && (values as any)['expiry-time']
          ? moment
              .utc(
                `${moment((values as any).expiry).format('YYYY-MM-DD')} ${moment
                  .utc((values as any)['expiry-time'])
                  .format('HH:mm')}`,
              )
              .format()
          : undefined,
      allowedTicketsIds: Array.isArray(values.allowedTicketsIds)
        ? values.allowedTicketsIds
        : values.allowedTicketsIds ? [values.allowedTicketsIds] : [],
      photoId: documentIds.length > 0 ? documentIds[0] : undefined,
      deleteObject: {
        minAge: (values as any).minAge === '' ? 1 : undefined,
        maxAge: (values as any).maxAge === '' ? 1 : undefined,
        limitPerFamily: (values as any).limitPerFamily === '' ? 1 : undefined,
        totalLimit: (values as any).totalLimit === '' ? 1 : undefined,
        photoId: !documentIds.length ? 1 : undefined,
      },
    }
    if (payload.deleteObject) {
      Object.keys(payload.deleteObject).forEach((key) => {
        if (payload.deleteObject[key] === undefined) {
          delete payload.deleteObject[key]
        }
      })
    }
    if (type === 'admission') {
      admissionTicketMutate(
        { body: payload, classId: classesData._id, ticketId: (data as any)?._id },
        {
          onSuccess: result => {
            setClassesData({ ...classesData, ...result })
            modalClose(false)
          },
        },
      )
    } else if (type === 'additional') {
      additionalTicketMutate(
        { body: payload, classId: classesData._id, ticketId: (data as any)?._id },
        {
          onSuccess: result => {
            setClassesData({ ...classesData, ...result })
            modalClose(false)
          },
        },
      )
    }
  }

  useEffect(() => {
    if (data && (data as any).ticketType !== 'one-time') {
      setSelectedDates(
        (data as any).days?.map((date: string) => moment(date).format('YYYY-MM-DD')) || [],
      )
      setEventTime({
        startTime: moment(
          convertMinutesToLocalTime((data as any).timings.startTime, 'HH:mm'),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime((data as any).timings.endTime, 'HH:mm'),
          'HH:mm',
        ),
      })
    }
  }, [data])

  useEffect(() => {
    if (data && (data as any).ticketType !== 'one-time') {
      setDefaultTime({
        startTime: moment(
          convertMinutesToLocalTime((data as any).timings.startTime, 'HH:mm'),
          'HH:mm',
        ),
        endTime: moment(
          convertMinutesToLocalTime((data as any).timings.endTime, 'HH:mm'),
          'HH:mm',
        ),
      })
    } else if (classesData?.timings) {
      setDefaultTime({
        startTime: moment(convertMinutesToLocalTime(classesData?.timings.startTime, 'HH:mm'), 'HH:mm'),
        endTime: moment(convertMinutesToLocalTime(classesData?.timings.endTime, 'HH:mm'), 'HH:mm'),
      })
    }
  }, [classesData?.timings, data])

  return (
    <div>
      <Modal
        open={modalOpen}
        onCancel={handleCancel}
        className="admission-ticket-modal"
        footer={false}
        centered
      >
        <div className="flex-center pt-20px justify-between ">
          <h2 className="w-600 text-18">
            {type === 'admission' ? 'Admission ticket' : 'Additional Ticket'}
          </h2>
          <img
            src={closeIcon}
            alt="close-button"
            onClick={handleCancel}
            className="close button pointer"
          />
        </div>
        <div className="h-30rem mt-20px overflow-auto-y overflow-hidden-x scroller">
          <Form form={form} layout="horizontal" initialValues={formSchema} onFinish={onFinish}>
            <Row gutter={10}>
              <Col>
                <label className="input-label required-input">Ticket title</label>
              </Col>
              <Col>
                <Form.Item className="form-control" name="name">
                  <Input placeholder="Ticket title" className="form-control-field" />
                </Form.Item>
              </Col>
            </Row>

            <Row gutter={10}>
              <Col>
                <label className="input-label required-input">Ticket type</label>
              </Col>
              <Col>
                <Form.Item className="pointer selector-input" name="ticketType">
                  <Select
                    className="select-input-field type-select-field"
                    placeholder="Select"
                    optionLabelProp="value"
                    onChange={handleTicketType}
                    options={[
                      {
                        value: 'open',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Open</h1>{' '}
                            <p className="grey-txt">Any number of days within period</p>
                          </>
                        ),
                      },
                      {
                        value: 'grouped',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Grouped</h1>{' '}
                            <p className="grey-txt">Weekly or monthly tickets</p>
                          </>
                        ),
                      },
                      {
                        value: 'schedule',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Schedule</h1>{' '}
                            <p className="grey-txt">Day of week can choose</p>
                          </>
                        ),
                      },
                      {
                        value: 'preselected',
                        label: (
                          <>
                            <h1 className="text-16 w-500">Preselected</h1>{' '}
                            <p className="grey-txt">Define the exact days of tickets</p>
                          </>
                        ),
                      },
                      {
                        value: 'one-time',
                        label: (
                          <>
                            <h1 className="text-16 w-500">One-time</h1>{' '}
                            <p className="grey-txt">Does not required date selection</p>
                          </>
                        ),
                      },
                    ]}
                  />
                </Form.Item>
              </Col>
            </Row>

            {ticketType === 'grouped' && (
              <Row>
                <Col>
                  <label className="input-label required-input">Period</label>
                </Col>
                <Col>
                  <Form.Item className="pointer selector-input" name="period">
                    <Select
                      className="select-input-field type-select-field"
                      placeholder="Select"
                      optionLabelProp="value"
                      onChange={handlePeriodChange}
                      options={[
                        { value: 'monthly', label: <p>Monthly</p> },
                        { value: 'weekly', label: <p>Weekly</p> },
                      ]}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {type === 'additional' && (
              <Row gutter={10}>
                <Col>
                  <label className="input-label required-input">Allowed tickets</label>
                </Col>
                <Col>
                  <Form.Item className="pointer selector-input" name="allowedTicketsIds">
                    <Select
                      className="select-input-field type-select-field"
                      placeholder="Select"
                      optionLabelProp="label"
                      options={(classesData?.admissionTickets || []).map((ticket: TicketsResp) => {
                        return { value: ticket._id, label: ticket.name }
                      })}
                      onChange={handleAdmissionTicketChange}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row>
              <Col span={24}>
                <label htmlFor="" className="input-label">
                  Expiration date
                </label>
                <Form.Item name="expiry">
                  <DatePicker
                    className="w-100 form-control-field"
                    placeholder="Expiration date"
                    disabledDate={disableOutOfRangeDates}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label htmlFor="" className="input-label">
                  Expiration Time
                </label>
                <Form.Item name="expiry-time">
                  <TimePicker
                    format={'hh:mm a'}
                    className="w-100 form-control-field"
                    placeholder="Expiration time"
                  />
                </Form.Item>
              </Col>
            </Row>

            {ticketType !== 'one-time' && (
              <DateandTimeInputs
                selectedDates={selectedDates}
                setSelectedDates={setSelectedDates}
                specialScheduleData={specialScheduleData}
                setSpecialScheduleData={setSpecialScheduleData}
                eventTime={eventTime}
                setEventTime={setEventTime}
                type={type as any}
                allowedTicketId={admissionTicketId}
                defaultTime={defaultTime}
                setDefaultTime={setDefaultTime}
              />
            )}

            {(ticketType === 'open' || ticketType === 'schedule') && (
              <Row className="mt-20px">
                <Col className="d-flex align-items-center">
                  <div className="relative" style={{ width: '100%', marginTop: '-14px' }}>
                    <span className="custom-field-label required-input">Max Registration days</span>
                  </div>
                  <Form.Item name="numberOfDays" style={{ width: '20%' }}>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="text-end br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            {ticketType === 'grouped' && (
              <Row className="mt-20px">
                <Col className="d-flex align-items-center">
                  <div className="relative" style={{ width: '100%', marginTop: '-14px' }}>
                    <span className="custom-field-label required-input">
                      {`Max Registration ${
                        period === 'weekly' ? 'weeks' : period === 'monthly' ? 'months' : ''
                      }`}
                    </span>
                  </div>
                  <Form.Item name="numberOfSelection" style={{ width: '20%' }}>
                    <Input
                      type="number"
                      min={0}
                      placeholder="0"
                      className="text-end br-12"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
              </Row>
            )}

            <Row className="mt-10px">
              <Col span={24}>
                <label htmlFor="" className="custom-field-label">
                  Description
                </label>
                <Form.Item name="description">
                  <Input.TextArea
                    className="note-field"
                    placeholder="Terms of usage of the ticket"
                    autoSize={{ minRows: 3, maxRows: 10 }}
                  />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col className="d-flex align-items-center">
                <div className="relative" style={{ width: '100%', marginTop: '-14px' }}>
                  <span className="custom-field-label required-input">Price</span>
                </div>
                <Form.Item name="price" style={{ width: '20%' }}>
                  <Input type="number" placeholder="0" className="text-end br-12" />
                </Form.Item>
              </Col>
            </Row>

            <Row>
              <Col span={24}>
                <label className="input-label required-input">Select colors</label>
                <TicketColors ticketColor={ticketColor} setTicketColor={setTicketColor} />
              </Col>
            </Row>

            <Row className="mt-20px">
              <Col span={24}>
                <label className="input-label required-input">Guests allowed to visit</label>
                <Form.Item className="form-control mb-0" name="guestAllowedToVisit">
                  <Radio.Group>
                    <Radio value={'all'} className="mr-40 radio">
                      All
                    </Radio>
                    <Radio value={'boys'} className="mr-40 radio">
                      Boys
                    </Radio>
                    <Radio value={'girls'} className="mr-40 radio">
                      Girls
                    </Radio>
                  </Radio.Group>
                </Form.Item>
              </Col>
            </Row>

            <div className="w-80p admission-ticket-inputs mt-20px">
              <Row>
                <Col>
                  <h4 className="b1 mb-15">Guests limitation</h4>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Age restrictions (min)</span>
                  </div>
                  <Form.Item name="minAge" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Age restrictions (max)</span>
                  </div>
                  <Form.Item name="maxAge" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Limit per Family</span>
                  </div>
                  <Form.Item name="limitPerFamily" style={{ width: '30%' }}>
                    <Input
                      type="number"
                      placeholder="0"
                      className="text-end br-5"
                      onWheel={e => e.currentTarget.blur()}
                    />
                  </Form.Item>
                </Col>
                <Col className="d-flex align-items-center">
                  <div style={{ width: '100%', marginTop: '-14px' }}>
                    <span>Total tickets limit</span>
                  </div>
                  <Form.Item name="totalLimit" style={{ width: '30%' }}>
                    <Input type="totalTicket" placeholder="0" className="text-end br-5 radius-12" />
                  </Form.Item>
                </Col>
              </Row>
            </div>

            <div className="w-80p">
              <label className="input-label">Add image</label>
              <UploadDocument
                documentIds={documentIds}
                setDocumentIds={setDocumentIds}
                multiple={false}
                docType="image/*"
                files={photos}
              />
            </div>

            <Row justify="end" className="mt-20px">
              <Button
                className="stepper-button"
                disabled={admissionTicketLoading || additionalTicketLoading}
                htmlType="submit"
              >
                {admissionTicketLoading || additionalTicketLoading ? (
                  <ButtonSpinner size={20} color="#000" />
                ) : (
                  'Save'
                )}
              </Button>
            </Row>
          </Form>
        </div>
      </Modal>
    </div>
  )
}

export default AdmissionTicket

